import React, { useState, useEffect } from 'react';
import { Companion, companionService } from '../services/companionService';
import Gallery from './Gallery';
import { fileService } from '../services/fileService';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from './ui/LoadingScreen';

// Inline MediaAsset interface to avoid import issues
interface MediaAsset {
  url: string;
  type: 'image' | 'video';
  id: string;
  poster?: string;
}

interface EditCompanionModalProps {
  companion: Companion;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedCompanion: any) => Promise<void>;
}

const EditCompanionModal: React.FC<EditCompanionModalProps> = ({
  companion,
  isOpen,
  onClose,
  onSave
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    nick_name: '',
    age: 20,
    description: '',
    about: '',
    birthday: '',
    is_public: true,
    gallery_assets: [] as MediaAsset[],
    categories: [] as string[],
    ethnicity: '',
    eye_color: '',
    hair_style: '',
    hair_color: '',
    body_type: '',
    occupation: '',
    hobby: '',
    traits_positive: [] as string[],
    traits_negative: [] as string[],
    personality_positive: [] as string[],
    personality_negative: [] as string[]
  });
  const [newAssetUrl, setNewAssetUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');


  // This is now handled globally in App.tsx, but keeping for extra safety
  useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (
        e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
        e.message.includes('ResizeObserver loop limit exceeded')
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };

    window.addEventListener('error', handleResizeObserverError);

    return () => {
      window.removeEventListener('error', handleResizeObserverError);
    };
  }, []);
  const [loading, setSaving] = useState(false);


  useEffect(() => {
    if (companion) {
      // Create unified gallery from all assets
      const galleryAssets: MediaAsset[] = [];

      // Add primary photo first if exists
      if (companion.assets?.primary_photo) {
        galleryAssets.push({
          url: companion.assets.primary_photo.presigned_url,
          type: 'image',
          id: 'primary_photo',
          isPrimary: true,
          primaryType: 'photo'
        } as MediaAsset & { isPrimary: boolean; primaryType: string });
      }

      // Add primary video second if exists
      if (companion.assets?.primary_video) {
        galleryAssets.push({
          url: companion.assets.primary_video.presigned_url,
          type: 'video',
          id: 'primary_video',
          isPrimary: true,
          primaryType: 'video'
        } as MediaAsset & { isPrimary: boolean; primaryType: string });
      }

      // Add gallery assets
      if (companion.assets?.gallery) {
        companion.assets.gallery.forEach((asset: any, index: number) => {
          galleryAssets.push({
            url: asset.presigned_url,
            type: asset.type === 'photo' ? 'image' : 'video',
            id: asset.asset_id || `asset_${index}`,
            isPrimary: false
          } as MediaAsset & { isPrimary: boolean });
        });
      }

      setFormData({
        // Separate name fields to match database structure
        first_name: typeof companion.name === 'object' && companion.name
          ? (companion.name.first_name || '')
          : String(companion.name || ''),
        last_name: typeof companion.name === 'object' && companion.name
          ? (companion.name.last_name || '')
          : '',
        nick_name: typeof companion.name === 'object' && companion.name
          ? (companion.name.nick_name || '')
          : '',
        age: companion.age || 20,
        description: companion.description || '',
        about: companion.about || '',
        birthday: companion.birthday ? 
          (typeof companion.birthday === 'string' ? 
            companion.birthday : 
            new Date(companion.birthday).toISOString().split('T')[0]
          ) : '',
        is_public: companion.is_public ?? true,
        gallery_assets: galleryAssets,
        categories: companion.categories || [],
        ethnicity: companion.attributes?.physical?.ethnicity || '',
        eye_color: companion.attributes?.physical?.eye_color || '',
        hair_style: companion.attributes?.physical?.hair_style || '',
        hair_color: companion.attributes?.physical?.hair_color || '',
        body_type: companion.attributes?.physical?.body_type || '',
        occupation: companion.attributes?.personal?.occupation || '',
        hobby: Array.isArray(companion.attributes?.personal?.hobby) ? companion.attributes.personal.hobby.join(', ') : '',
        traits_positive: companion.traits?.positive || [],
        traits_negative: companion.traits?.negative || [],
        personality_positive: companion.personality?.positive || [],
        personality_negative: companion.personality?.negative || []
      });
    }
  }, [companion]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddAsset = () => {
    if (newAssetUrl.trim() && !formData.gallery_assets.some(asset => asset.url === newAssetUrl.trim())) {
      const newAsset: MediaAsset & { isPrimary: boolean } = {
        url: newAssetUrl.trim(),
        type: isVideoUrl(newAssetUrl.trim()) ? 'video' : 'image',
        id: `asset_${Date.now()}`,
        isPrimary: false
      };

      setFormData(prev => ({
        ...prev,
        gallery_assets: [...prev.gallery_assets, newAsset]
      }));
      setNewAssetUrl('');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Check authentication first
    if (!user) {
      alert('Please log in to upload files.');
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('Authentication expired. Please log in again.');
      return;
    }

    setIsUploading(true);
    setUploadProgress('');

    let successCount = 0;
    let failedFiles: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
        
        // Validate file type
        if (!fileService.isValidFileType(file)) {
          alert(`Invalid file type: ${file.name}. Please upload images (JPEG, PNG, GIF, WebP) or videos (MP4, WebM, OGG, AVI, MOV).`);
          failedFiles.push(`${file.name} (invalid type)`);
          continue;
        }

        // Check file size (limit to 50MB)
        if (file.size > 50 * 1024 * 1024) {
          alert(`File too large: ${file.name}. Please upload files smaller than 50MB.`);
          failedFiles.push(`${file.name} (too large)`);
          continue;
        }

        setUploadProgress(`Uploading ${file.name} (${fileSizeMB}MB)... ${i + 1}/${files.length}`);

        try {
          // Check if companion has an ID
          if (!companion.companion_id) {
            throw new Error('Companion ID is required for uploading assets');
          }

          // Upload the file and automatically add to companion assets
          const fileType = fileService.getFileTypeCategory(file);
          const result = await fileService.uploadAndAddAsset(
            file, 
            companion.companion_id, 
            fileType === 'video' ? 'video' : 'image',
            false // Not primary by default
          );
          
          // Add to local gallery state
          const newAsset: MediaAsset & { isPrimary: boolean } = {
            url: result.download_url,
            type: fileType === 'video' ? 'video' : 'image',
            id: `asset_${Date.now()}_${Math.random()}`,
            isPrimary: false
          };
          
          setFormData(prev => ({
            ...prev,
            gallery_assets: [...prev.gallery_assets, newAsset]
          }));

          successCount++;
          console.log(`✅ Successfully uploaded ${file.name} (${successCount}/${files.length})`);

          // Add delay between uploads to reduce server stress
          if (i < files.length - 1) {
            setUploadProgress(`Upload complete. Preparing next file... (${i + 1}/${files.length})`);
            await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
          }

        } catch (uploadError: any) {
          console.error('Upload failed for file:', file.name, uploadError);
          failedFiles.push(`${file.name} (${uploadError.message})`);

          // Continue with next file instead of stopping all uploads
        }
      }

      // Show completion summary
      if (successCount > 0 || failedFiles.length > 0) {
        let message = '';
        if (successCount > 0) {
          message += `✅ Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}`;
        }
        if (failedFiles.length > 0) {
          if (message) message += '\n\n';
          message += `❌ Failed to upload ${failedFiles.length} file${failedFiles.length > 1 ? 's' : ''}:\n${failedFiles.join('\n')}`;
        }
        if (message) {
          alert(message);
        }
      }

    } catch (error: any) {
      console.error('Upload process failed:', error);
      alert(`Upload process failed: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress('');
      // Reset the file input
      event.target.value = '';
    }
  };



  const handleRemoveAsset = async (index: number) => {
    const asset = formData.gallery_assets[index];
    if (!asset || !companion.companion_id) {
      console.error('Cannot remove asset: missing asset or companion ID');
      return;
    }

    try {
      // Extract asset ID from URL
      // URLs look like: https://domain.com/ai-chat-images/filename.jpg?params
      // or: https://domain.com/ai-chat-images/filename.jpg?params
      const url = new URL(asset.url);
      const pathParts = url.pathname.split('/');
      const filename = pathParts[pathParts.length - 1]; // Get the last part (filename)
      
      // The asset ID in the database is just the filename
      const assetId = filename;
      
      console.log('Attempting to delete asset:', { assetId, companionId: companion.companion_id });
      
      // Call API to delete from backend
      await companionService.deleteCompanionAsset(companion.companion_id, assetId);
      
      // Remove from local state only after successful API call
      setFormData(prev => ({
        ...prev,
        gallery_assets: prev.gallery_assets.filter((_, i) => i !== index)
      }));
      
      console.log('Asset deleted successfully');
      
    } catch (error) {
      console.error('Failed to delete asset:', error);
      alert('Failed to delete asset. Please try again.');
    }
  };

  const handleReorderAssets = (fromIndex: number, toIndex: number) => {
    setFormData(prev => {
      const newAssets = [...prev.gallery_assets];
      const [draggedItem] = newAssets.splice(fromIndex, 1);
      newAssets.splice(toIndex, 0, draggedItem);
      return {
        ...prev,
        gallery_assets: newAssets
      };
    });
  };

  const handleSetPrimary = (index: number, type: 'photo' | 'video') => {
    setFormData(prev => {
      const newAssets = [...prev.gallery_assets];
      
      // Remove primary status from all assets of this type
      newAssets.forEach((asset, i) => {
        if ((asset as any).isPrimary && (asset as any).primaryType === type) {
          (newAssets[i] as any).isPrimary = false;
          (newAssets[i] as any).primaryType = undefined;
        }
      });
      
      // Set the selected asset as primary
      (newAssets[index] as any).isPrimary = true;
      (newAssets[index] as any).primaryType = type;
      
      return {
        ...prev,
        gallery_assets: newAssets
      };
    });
  };

  const isVideoUrl = (url: string) => {
    return url.toLowerCase().includes('.mp4') || url.toLowerCase().includes('.webm') || url.toLowerCase().includes('.mov');
  };





  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Extract primary assets and more assets from gallery
      console.log('All gallery assets before extraction:', formData.gallery_assets);

      const primaryPhotoAsset = formData.gallery_assets.find(asset =>
        (asset as any).isPrimary && (asset as any).primaryType === 'photo'
      );
      const primaryPhoto = primaryPhotoAsset?.url || '';

      const primaryVideoAsset = formData.gallery_assets.find(asset =>
        (asset as any).isPrimary && (asset as any).primaryType === 'video'
      );
      const primaryVideo = primaryVideoAsset?.url || '';

      console.log('Extracted primary assets:', {
        primaryPhotoAsset,
        primaryPhoto,
        primaryVideoAsset,
        primaryVideo
      });

      // Note: Gallery assets are now handled through the unified gallery system

      // Structure the data to match the updated backend AdminCompanionUpdate model
      // Start with minimal data to isolate validation issues
      const updatedData = {
        // Separate name fields (new backend supports this)
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        nick_name: formData.nick_name || null,
        
        // Basic fields - ensure all are proper types
        description: formData.description || null,
        about: formData.about || null,
        birthday: formData.birthday ? String(formData.birthday) : null,
        age: formData.age || null,
        is_public: Boolean(formData.is_public),
        categories: Array.isArray(formData.categories) ? formData.categories : [],
        
        // Primary assets (for setting primary photo/video)
        primary_photo: primaryPhoto || null,
        primary_video: primaryVideo || null,
        
        // Attributes as nested object - ensure no undefined values
        attributes: {
          physical: {
            ethnicity: formData.ethnicity || null,
            eye_color: formData.eye_color || null,
            hair_style: formData.hair_style || null,
            hair_color: formData.hair_color || null,
            body_type: formData.body_type || null
          },
          personal: {
            occupation: formData.occupation || null,
            hobby: formData.hobby ? 
              (Array.isArray(formData.hobby) ? 
                formData.hobby : 
                formData.hobby.split(',').map(h => h.trim()).filter(h => h)
              ) : []
          }
        },
        
        // Traits and personality - ensure arrays
        traits: {
          positive: Array.isArray(formData.traits_positive) ? formData.traits_positive : [],
          negative: Array.isArray(formData.traits_negative) ? formData.traits_negative : []
        },
        personality: {
          positive: Array.isArray(formData.personality_positive) ? formData.personality_positive : [],
          negative: Array.isArray(formData.personality_negative) ? formData.personality_negative : []
        }
      };

      // Debug logging to see what data is being sent
      console.log('🔍 Debug: Form data being sent to backend:');
      console.log('updatedData:', JSON.stringify(updatedData, null, 2));
      console.log('🔍 Data types:');
      Object.keys(updatedData).forEach(key => {
        const value = (updatedData as any)[key];
        console.log(`${key}: ${typeof value} = ${JSON.stringify(value)}`);
      });

      await onSave(updatedData);
      handleSafeClose();
    } catch (error) {
      console.error('Failed to save companion:', error);
      alert('Failed to save companion. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Safe close handler that suppresses ResizeObserver errors
  const handleSafeClose = () => {
    try {
      // Use setTimeout to defer the close operation
      setTimeout(() => {
        onClose();
      }, 0);
    } catch (error) {
      // Suppress any errors during close
      console.warn('Modal close error suppressed:', error);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-dark-800 rounded-2xl w-full max-w-7xl max-h-[90vh] flex flex-col">
        <form onSubmit={handleSubmit} className="h-full flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-dark-600 flex-shrink-0 bg-dark-800">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Edit Companion</h2>
              <button
                type="button"
                onClick={handleSafeClose}
                className="text-dark-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 140px)' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Basic Information</h3>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Yumi"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Yoom"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Nickname</label>
                  <input
                    type="text"
                    value={formData.nick_name}
                    onChange={(e) => handleInputChange('nick_name', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., YuYo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Age</label>
                  <input
                    type="number"
                    value={formData.age}
                    onChange={(e) => handleInputChange('age', parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    min="18"
                    max="100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Categories</label>
                  <input
                    type="text"
                    value={formData.categories.join(', ')}
                    onChange={(e) => handleInputChange('categories', e.target.value.split(', ').filter(c => c.trim()))}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Realistic, Female"
                  />
                  <div className="text-xs text-dark-400 mt-1">
                    Separate multiple categories with commas
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_public"
                    checked={formData.is_public}
                    onChange={(e) => handleInputChange('is_public', e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="is_public" className="text-sm font-medium text-dark-300">
                    Public (visible to users)
                  </label>
                </div>
              </div>

              {/* Physical Attributes */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Physical Attributes</h3>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Ethnicity</label>
                  <input
                    type="text"
                    value={formData.ethnicity}
                    onChange={(e) => handleInputChange('ethnicity', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Asian, Caucasian, Hispanic"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Eye Color</label>
                  <input
                    type="text"
                    value={formData.eye_color}
                    onChange={(e) => handleInputChange('eye_color', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Brown, Blue, Green"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Hair Style</label>
                  <input
                    type="text"
                    value={formData.hair_style}
                    onChange={(e) => handleInputChange('hair_style', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Long wavy, Short athletic"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Hair Color</label>
                  <input
                    type="text"
                    value={formData.hair_color}
                    onChange={(e) => handleInputChange('hair_color', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Black, Brown, Blonde"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Body Type</label>
                  <input
                    type="text"
                    value={formData.body_type}
                    onChange={(e) => handleInputChange('body_type', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Athletic, Petite, Average"
                  />
                </div>
              </div>

              {/* Personal Attributes */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Personal Attributes</h3>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Occupation</label>
                  <input
                    type="text"
                    value={formData.occupation}
                    onChange={(e) => handleInputChange('occupation', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Software Engineer, Doctor"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Hobby</label>
                  <input
                    type="text"
                    value={formData.hobby}
                    onChange={(e) => handleInputChange('hobby', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Cooking, Yoga, Reading"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Birthday</label>
                  <input
                    type="date"
                    value={formData.birthday}
                    onChange={(e) => handleInputChange('birthday', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                  />
                </div>
              </div>

              {/* Traits & Personality */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4">Traits & Personality</h3>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Positive Traits</label>
                  <input
                    type="text"
                    value={formData.traits_positive.join(', ')}
                    onChange={(e) => handleInputChange('traits_positive', e.target.value.split(', ').filter(t => t.trim()))}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Creative, Cat Lover, Ambitious"
                  />
                  <div className="text-xs text-dark-400 mt-1">
                    Separate multiple traits with commas
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Negative Traits</label>
                  <input
                    type="text"
                    value={formData.traits_negative.join(', ')}
                    onChange={(e) => handleInputChange('traits_negative', e.target.value.split(', ').filter(t => t.trim()))}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Clumsy, Lazy, Impatient"
                  />
                  <div className="text-xs text-dark-400 mt-1">
                    Separate multiple traits with commas
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Positive Personality</label>
                  <input
                    type="text"
                    value={formData.personality_positive.join(', ')}
                    onChange={(e) => handleInputChange('personality_positive', e.target.value.split(', ').filter(p => p.trim()))}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Empathy, Honesty, Optimism"
                  />
                  <div className="text-xs text-dark-400 mt-1">
                    Separate multiple personality traits with commas
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Negative Personality</label>
                  <input
                    type="text"
                    value={formData.personality_negative.join(', ')}
                    onChange={(e) => handleInputChange('personality_negative', e.target.value.split(', ').filter(p => p.trim()))}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    placeholder="e.g., Dishonesty, Stubbornness, Pessimism"
                  />
                  <div className="text-xs text-dark-400 mt-1">
                    Separate multiple personality traits with commas
                  </div>
                </div>
              </div>

              {/* Description & About */}
              <div className="md:col-span-2 lg:col-span-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    rows={3}
                    placeholder="Brief description of the companion..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-dark-300 mb-2">About</label>
                  <textarea
                    value={formData.about}
                    onChange={(e) => handleInputChange('about', e.target.value)}
                    className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                    rows={3}
                    placeholder="Additional information about the companion..."
                  />
                </div>
              </div>

              {/* Gallery */}
              <div className="md:col-span-2 lg:col-span-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">Gallery</h3>
                  <div className="text-xs text-dark-400 bg-dark-700 px-3 py-1 rounded-full">
                    💡 Drag to reorder • Use buttons to set primary
                  </div>
                </div>

                {/* Add New Asset */}
                <div className="mb-4 space-y-4">
                  {/* File Upload Section */}
                  <div>
                    <label className="block text-sm font-medium text-dark-300 mb-2">
                      📁 Upload from Computer
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        onChange={handleFileUpload}
                        disabled={isUploading || !user}
                        className="hidden"
                        id="file-upload-input"
                      />
                      <label
                        htmlFor="file-upload-input"
                        className={`flex-1 px-4 py-2 border-2 border-dashed border-dark-600 rounded-lg text-center cursor-pointer transition-colors ${
                          isUploading 
                            ? 'bg-dark-800 text-dark-400 cursor-not-allowed' 
                            : !user 
                            ? 'bg-red-900/20 border-red-600 text-red-400 cursor-not-allowed'
                            : 'hover:border-accent-purple hover:bg-dark-800/50 text-dark-300'
                        }`}
                      >
                        {isUploading ? (
                          <LoadingScreen
                            title=""
                            message={uploadProgress || 'Uploading...'}
                            variant="minimal"
                            size="sm"
                            showDots={false}
                          />
                        ) : !user ? (
                          <span className="flex items-center justify-center gap-2">
                            🔒 Please log in to upload files
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            📤 Click to upload images or videos
                            <span className="text-xs text-dark-400">(Max 50MB each)</span>
                          </span>
                        )}
                      </label>
                    </div>
                    <div className="text-xs text-dark-400 mt-1">
                      Supported: JPEG, PNG, GIF, WebP, MP4, WebM, OGG, AVI, MOV
                    </div>
                    <div className="text-xs text-amber-400 mt-1 bg-amber-900/20 px-2 py-1 rounded">
                      ⚠️ Note: Direct upload may fail due to CORS. Use URL method below as alternative.
                    </div>
                  </div>

                  {/* URL Input Section */}
                  <div>
                    <label className="block text-sm font-medium text-dark-300 mb-2">
                      🔗 Add from URL
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={newAssetUrl}
                        onChange={(e) => setNewAssetUrl(e.target.value)}
                        className="flex-1 px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
                        placeholder="https://example.com/image.jpg or video.mp4"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAsset())}
                        disabled={isUploading}
                      />
                      <button
                        type="button"
                        onClick={handleAddAsset}
                        disabled={isUploading}
                        className="px-4 py-2 bg-accent-purple text-white rounded-lg hover:bg-accent-purple/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add URL
                      </button>
                    </div>
                  </div>
                </div>

                {/* Gallery Component */}
                <Gallery
                  assets={formData.gallery_assets}
                  editable={true}
                  showControls={true}
                  onReorder={handleReorderAssets}
                  onDelete={handleRemoveAsset}
                  onSetPrimary={handleSetPrimary}

                  gridCols={6}
                  className="mb-4"
                  separateMediaTypes={true}
                />

                {/* Asset Type Summary */}
                <div className="mt-4 p-3 bg-dark-800 rounded-lg">
                  <div className="text-sm font-medium text-dark-300 mb-2">Gallery Summary</div>
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <span className="text-blue-400">📷 Images:</span>
                      <span className="text-white ml-1">
                        {formData.gallery_assets.filter(asset =>
                          asset.type === 'image' || !isVideoUrl(asset.url)
                        ).length}
                      </span>
                    </div>
                    <div>
                      <span className="text-purple-400">🎥 Videos:</span>
                      <span className="text-white ml-1">
                        {formData.gallery_assets.filter(asset =>
                          asset.type === 'video' || isVideoUrl(asset.url)
                        ).length}
                      </span>
                    </div>
                    <div>
                      <span className="text-green-400">📊 Total:</span>
                      <span className="text-white ml-1">{formData.gallery_assets.length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-dark-600 flex gap-3 justify-end flex-shrink-0 bg-dark-800">
            <button
              type="button"
              onClick={handleSafeClose}
              className="px-6 py-2 bg-dark-600 text-white rounded-lg hover:bg-dark-700 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-accent-purple text-white rounded-lg hover:bg-accent-purple/80 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>


    </div>
  );
};

export default EditCompanionModal;